import React from 'react';
import {
  Pill,
  Tag,
} from '../pages/dashboard/dashboard-units-components/dashboard-units.styled';
import { Caption } from './iq-shared.styled';
import { countOf } from '../../function-utils/plural';
import { clockTime, formatIn } from '../../function-utils/building-time';
import { useClock } from '../../context/clock-context';
import { ClockNote } from '../clock-note/clock-note';
import {
  AnswerText,
  AnswerLabel,
  MetaRow,
  TableScroll,
  Table,
  Mono,
  Absent,
  Caveat,
} from './iq-answer.styled';
import {
  AskResponse,
  AskPackageRow,
  AskRecentRow,
  AskOccupantRow,
  AskLocatedPersonRow,
} from '../../interfaces/iq-interfaces';

/**
 * ONE renderer for an Ask answer, used by BOTH ask surfaces.
 *
 * There were two byte-identical copies of this JSX — iq-ask-panel.tsx and
 * iq-chat.tsx — and that is precisely why the newline bug survived: IqAskPanel
 * is only reachable by deep-linking /iq-handover (the main-menu tile calls
 * openIqChat('full'), main-menu.tsx:214), so a defect there never shows up in
 * casual testing. Adding three new response modes to two copies would have
 * doubled that surface again. One component, two call sites.
 *
 * WHAT THIS COMPONENT IS RESPONSIBLE FOR SAYING OUT LOUD:
 *   • a LIST answer is exact and was computed in SQL — render the rows;
 *   • a SEMANTIC answer is a RETRIEVED SUBSET and must never read as a total;
 *   • found:false means the entries that were searched do not mention it, which
 *     is a statement about the search, NOT about the world.
 */

// Copy lives in module constants rather than in JSX text nodes. Two reasons:
// react/no-unescaped-entities (error severity via plugin:react/recommended)
// rejects a bare apostrophe in JSX, and every label this surface owns is then
// readable in one place instead of buried in a ternary.
const COPY = {
  // Kept BYTE-IDENTICAL to the string both panels already rendered — this is an
  // existing user-facing string and the refusal path is unchanged by WP-16.
  refusalFallback: 'That question is outside what Lobbero IQ can answer.',

  packagesTitle: 'Parcels logged',
  occupantsTitle: 'Current occupants',
  locateTitle: 'Resident lookup',

  // WP-19 — the entity families.
  buildingsTitle: 'Buildings',
  unitsTitle: 'Units',
  staffTitle: 'Staff',
  accountTitle: 'Your account',
  capabilitiesTitle: 'What Lobbero IQ can answer',
  // An as-of answer has no window. Saying so is not decoration: the same panel
  // prints a date range for a list_packages answer, and a reader who has seen
  // that will assume the absence of one here means something was lost.
  asOfNow: 'As of now',

  orgWide: 'All buildings you can see',
  thisBuilding: 'This building only',

  windowShift: 'Window: your own shift',
  windowLast12h: 'Window: the last 12 hours',

  truncated: 'Capped — more rows exist than are shown',

  // A semantic answer is retrieval, not arithmetic — WHEN IT IS RETRIEVAL.
  //
  // `source_summary.sampled` used to be hard-coded true on this path, and the
  // caveat below was written for that. A question that names a narrow period
  // ("today", "yesterday", "on my shift") is now READ IN FULL rather than
  // vector-sampled, so the two cases need two sentences: telling somebody their
  // answer came from a selection when every entry in the period is in front of
  // them sends them off to search a log they have already been shown all of.
  semanticSubset:
    'Read from a retrieved selection of log entries, not from every entry in the period. Use it as a lead, not as a count.',
  semanticFullRead:
    'Read from every log entry and parcel in this period, in order. Nothing was left out by relevance.',
  semanticNotFound:
    'Nothing in the entries that were searched mentions this. That is a statement about those entries — it is not a record that nothing happened. Widen the period or search the log directly before concluding.',
  semanticNotFoundFullRead:
    'Nothing in this period mentions it, and this period was read in full — so this is a statement about the record, not about what was retrieved.',
  notFoundPill: 'No mention in this period',
  subsetPill: 'Retrieved selection',
  fullReadPill: 'Read in full',

  // Sits under a locate_person table for a building-scoped caller. Without it a
  // reader wonders whether a building was hidden; with it they know the answer
  // is unit-level by design (ask-facts.js:159-175).
  unitOnly:
    'Units are shown without a building because this answer is scoped to the buildings you work in.',

  typed: 'typed',
  stillInside: 'still inside',
  primary: 'primary',
  current: 'current',
  former: 'former resident',
};

const STATUS_LABEL: Record<string, string> = {
  awaiting_pickup: 'awaiting pickup',
  picked_up: 'picked up',
  returned: 'returned',
  void: 'void',
};

const statusTone = (s: string): 'success' | 'warning' | 'neutral' => {
  if (s === 'picked_up') return 'success';
  if (s === 'awaiting_pickup') return 'warning';

  return 'neutral';
};

// The REFUSAL path sends from:'' and to:'' (iq.js:697), so the empty guard is
// load-bearing and not defensive padding — without it this prints "Invalid Date".
const fmtDate = (iso: string, tz: string): string =>
  formatIn(iso, tz, { day: '2-digit', month: 'short' });

const fmtTime = (iso: string | null, tz: string): string => clockTime(iso, tz);

// A parcel name that was TYPED at the desk and one that was MATCHED to a
// resident are different levels of certainty (ask-facts.js:103-107). Only the
// weaker state is marked: tagging every matched row too would be noise, and the
// unmarked default is the one the desk expects.
const isMatched = (resolution: string): boolean =>
  resolution === 'single_match' || resolution === 'picked';

const Dash: React.FC = () => <Absent>—</Absent>;

const PackagesTable: React.FC<{
  rows: AskPackageRow[];
  tz: string;
  showBuilding: boolean;
}> = ({ rows, tz, showBuilding }) => {
  // The "Collected by" column appears only when a parcel in this list actually
  // was collected. A column of dashes reads as missing data; the Status column
  // already says "awaiting pickup", so nothing is concealed by its absence.
  const showCollected = rows.some(r => !!r.releasedTo);

  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <th>Time</th>
            {showBuilding ? <th>Building</th> : null}
            <th>Unit</th>
            <th>Carrier</th>
            <th>For</th>
            {showCollected ? <th>Collected by</th> : null}
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td data-label="Time">
                <Mono>{fmtTime(r.loggedAt, tz) || '—'}</Mono>
              </td>
              {showBuilding ? (
                <td data-label="Building">{r.building || <Dash />}</td>
              ) : null}
              <td data-label="Unit">{r.unit || <Dash />}</td>
              <td data-label="Carrier">
                {/* A multi-parcel drop is one row with a count, so the quantity
                    rides next to the carrier rather than costing a column that
                    would be "1" on almost every row. */}
                {r.count > 1 ? `${r.count}× ` : ''}
                {r.carrier || <Dash />}
              </td>
              <td data-label="For">
                {r.recipient || <Dash />}
                {r.recipient && !isMatched(r.recipientResolution) ? (
                  <> <Tag tone="neutral">{COPY.typed}</Tag></>
                ) : null}
              </td>
              {showCollected ? (
                <td data-label="Collected by">{r.releasedTo || <Dash />}</td>
              ) : null}
              <td data-label="Status">
                <Pill tone={statusTone(r.status)}>
                  {STATUS_LABEL[r.status] || r.status}
                </Pill>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
};

const OccupantsTable: React.FC<{
  rows: AskOccupantRow[];
  showBuilding: boolean;
}> = ({ rows, showBuilding }) => (
  <TableScroll>
    <Table>
      <thead>
        <tr>
          {showBuilding ? <th>Building</th> : null}
          <th>Unit</th>
          <th>Name</th>
          <th>Relationship</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id}>
            {showBuilding ? (
              <td data-label="Building">{r.building || <Dash />}</td>
            ) : null}
            <td data-label="Unit">{r.unit || <Dash />}</td>
            <td data-label="Name">
              {r.name || <Dash />}
              {r.isPrimary ? (
                <> <Tag tone="brand">{COPY.primary}</Tag></>
              ) : null}
            </td>
            <td data-label="Relationship">{r.relationship || <Dash />}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  </TableScroll>
);

const LocateTable: React.FC<{
  rows: AskLocatedPersonRow[];
  showBuilding: boolean;
}> = ({ rows, showBuilding }) => (
  <TableScroll>
    <Table>
      <thead>
        <tr>
          <th>Name</th>
          <th>Unit</th>
          {/* NO EMPTY BUILDING COLUMN. For a building-scoped caller the server
              never assembles the building at all (ask-facts.js:211-215), so a
              header with blank cells under it would tell the reader something
              was withheld from them. The unit alone IS the answer. */}
          {showBuilding ? <th>Building</th> : null}
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.name}-${r.unit}-${i}`}>
            <td data-label="Name">{r.name || <Dash />}</td>
            <td data-label="Unit">{r.unit || <Dash />}</td>
            {showBuilding ? (
              <td data-label="Building">{r.building || <Dash />}</td>
            ) : null}
            <td data-label="Status">
              {r.current ? (
                <Pill tone="success">{COPY.current}</Pill>
              ) : (
                <Pill tone="neutral">{COPY.former}</Pill>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </TableScroll>
);

const LIST_TITLE: Record<string, string> = {
  list_packages: COPY.packagesTitle,
  list_occupants: COPY.occupantsTitle,
  locate_person: COPY.locateTitle,
  list_buildings: COPY.buildingsTitle,
  list_units: COPY.unitsTitle,
  list_staff: COPY.staffTitle,
};

// THE ONLY CASTS IN THIS FILE, AND THEY ARE DELIBERATE. The discriminant lives
// on the RESPONSE (`metric`), not on the row — the server sends a homogeneous
// array whose shape was decided one level up (iq.js:811-814). Narrowing once,
// against that field, is honest; sniffing each item's keys would be fiction.
//
// WP-19 CHANGED THE FALLBACK, AND THAT IS THE IMPORTANT PART OF THIS EDIT.
// This function used to END with the LocateTable, so ANY metric it did not
// recognise was rendered as a resident-lookup table: three columns read off
// objects that have none of them, producing a table of blanks under headers that
// describe data the row never contained. With four new list metrics that is not
// a hypothetical. The unknown case now renders the server's own deterministic
// text, which is a complete, correct answer — the same text a script or a
// copy-paste into an email receives.
/** THE RECENT-ENTRY LOG. "Who entered today" lands here.
 *
 *  A table rather than a sentence, because that is what the question asks for: a
 *  transparent, readable log of who came through and when. Every value is the
 *  server's — decrypted names, the row's own time, its unit and its note — so
 *  nothing here is a model restating rows it was shown.
 *
 *  Built from the SAME primitives as the other three tables (TableScroll /
 *  Table / Mono / Dash / Tag), so a log row looks like a parcel row. */
const RecentTable: React.FC<{
  rows: AskRecentRow[];
  tz: string;
}> = ({ rows, tz }) => {
  // Same rule the parcel table applies to "Collected by": a column of dashes
  // reads as missing data, so the note column appears only if any row has one.
  const showNote = rows.some(r => !!r.note);

  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <th>Time</th>
            <th>Who</th>
            <th>What</th>
            <th>Unit</th>
            {showNote ? <th>Note</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td data-label="Time">
                <Mono>{fmtTime(r.at, tz) || '—'}</Mono>
              </td>
              <td data-label="Who">
                {r.people.length ? r.people.join(', ') : <Dash />}
                {/* Still inside for a person, still open for a building
                    activity — the same fact, from the same field, that the
                    handover reports as a follow-up. */}
                {r.open === true ? (
                  <> <Tag tone="neutral">{COPY.stillInside}</Tag></>
                ) : null}
              </td>
              <td data-label="What">{r.kind || <Dash />}</td>
              <td data-label="Unit">{r.unit || <Dash />}</td>
              {showNote ? <td data-label="Note">{r.note || <Dash />}</td> : null}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
};

const renderRows = (
  res: AskResponse,
  tz: string,
  orgWide: boolean
): React.ReactElement => {
  const rows = res.rows || [];
  if (res.metric === 'list_recent') {
    return <RecentTable rows={rows as AskRecentRow[]} tz={tz} />;
  }
  if (res.metric === 'list_packages') {
    return (
      <PackagesTable
        rows={rows as AskPackageRow[]}
        tz={tz}
        showBuilding={orgWide}
      />
    );
  }
  if (res.metric === 'list_occupants') {
    return (
      <OccupantsTable rows={rows as AskOccupantRow[]} showBuilding={orgWide} />
    );
  }
  if (res.metric === 'locate_person') {
    return (
      <LocateTable rows={rows as AskLocatedPersonRow[]} showBuilding={orgWide} />
    );
  }

  return <AnswerText>{res.answer}</AnswerText>;
};

/** WHAT THE ANSWER COVERS, on every shape (owner decision D-20).
 *
 *  The server already puts this sentence inside `answer` for the entity
 *  families, so the badge is the same claim in the place a reader looks first —
 *  and it is the ONLY place a count or a narrative answer states its scope,
 *  because those two shapes are a single sentence with nowhere to put it. */
const ScopeRow: React.FC<{ res: AskResponse }> = ({ res }) =>
  res.scope ? (
    <MetaRow>
      <Tag tone={res.scope.org_wide ? 'brand' : 'neutral'}>
        {res.scope.label}
      </Tag>
      {res.as_of ? <Tag tone="neutral">{COPY.asOfNow}</Tag> : null}
    </MetaRow>
  ) : null;

/** The FACTS shape: one structured object, rendered from the server's text.
 *
 *  Deliberately NOT a bespoke stat grid. The text is generated from the same
 *  numbers, states its own scope, and omits the billing block entirely for a
 *  caller who may not see it — a hand-built grid would have to reproduce all
 *  three behaviours and would be the thing that drifts. */
const FactsAnswer: React.FC<{ res: AskResponse }> = ({ res }) => (
  <>
    <AnswerLabel>
      {res.metric === 'capabilities'
        ? COPY.capabilitiesTitle
        : COPY.accountTitle}
    </AnswerLabel>
    {/* NO scope badge on a capability list. What IQ can answer is decided by
        the asker's PERMISSIONS, not by their buildings, so "1 building" beside
        it would imply the list varies by building. It does not. */}
    {res.metric === 'capabilities' ? null : <ScopeRow res={res} />}
    <AnswerText>{res.answer}</AnswerText>
  </>
);

const ListAnswer: React.FC<{ res: AskResponse; tz: string }> = ({ res, tz }) => {
  const rows = res.rows || [];
  const orgWide = res.org_wide === true;
  const title = LIST_TITLE[res.metric || ''] || COPY.locateTitle;

  // An empty list still has a real answer, and the server already wrote it in a
  // sentence ("No parcels logged in that period."). Rendering an empty table
  // instead would be worse than the text it replaced.
  if (!rows.length) return <AnswerText>{res.answer}</AnswerText>;

  return (
    <>
      <AnswerLabel>{title}</AnswerLabel>
      <MetaRow>
        <Tag tone="neutral">{`${rows.length}`}</Tag>
        {/* Scope is stated on every list, both ways round. "This building only"
            is as much a fact about the answer as "all buildings" is, and a
            reader who is only told about one of them cannot calibrate the
            other.

            WP-19 PREFERS THE SERVER'S OWN SENTENCE when it is present, because
            the two-value version was wrong for the middle case: a manager
            scoped to THREE of an organisation's ten buildings was told "This
            building only", which is neither true nor the reason their number
            looks small. `scope.label` counts them. The old copy stays as the
            fallback for a server that has not shipped the field yet. */}
        <Tag tone={orgWide ? 'brand' : 'neutral'}>
          {res.scope
            ? res.scope.label
            : orgWide
              ? COPY.orgWide
              : COPY.thisBuilding}
        </Tag>
        {res.window_source === 'shift' ? (
          <Tag tone="neutral">{COPY.windowShift}</Tag>
        ) : null}
        {res.window_source === 'last_12h' ? (
          <Tag tone="neutral">{COPY.windowLast12h}</Tag>
        ) : null}
        {res.source_summary.sampled ? (
          <Tag tone="warning">{COPY.truncated}</Tag>
        ) : null}
      </MetaRow>

      {renderRows(res, tz, orgWide)}

      {res.metric === 'locate_person' && !orgWide ? (
        <Caveat>{COPY.unitOnly}</Caveat>
      ) : null}

      {res.metric === 'list_packages' ? (
        <Caption>
          {`${fmtDate(res.source_summary.from, tz)}–${fmtDate(
            res.source_summary.to,
            tz
          )}`}
        </Caption>
      ) : null}
    </>
  );
};

const SemanticAnswer: React.FC<{ res: AskResponse; tz: string }> = ({
  res,
  tz,
}) => (
  <>
    {/* The pill is first because it changes how the sentence beneath it should
        be read, and a caveat that arrives after the claim has already been
        believed is not a caveat. */}
    <MetaRow>
      {res.found === false ? (
        <Pill tone="warning">{COPY.notFoundPill}</Pill>
      ) : res.source_summary.sampled ? (
        <Pill tone="neutral">{COPY.subsetPill}</Pill>
      ) : (
        <Pill tone="success">{COPY.fullReadPill}</Pill>
      )}
    </MetaRow>
    <AnswerText>{res.answer}</AnswerText>
    <Caveat>
      {res.found === false
        ? res.source_summary.sampled
          ? COPY.semanticNotFound
          : COPY.semanticNotFoundFullRead
        : res.source_summary.sampled
        ? COPY.semanticSubset
        : COPY.semanticFullRead}
    </Caveat>
    {/* NO "· showing 10" ON THIS PATH. The retrieval cap is RETRIEVE_LIMIT, not
        ten — printing a number that is not the cap would be a precise, confident
        lie. The count of what was read is enough.

        The PERIOD LABEL leads when the question named one. Two dates are a fact
        the reader has to re-derive into "…so that was today"; the word they
        typed is the thing they asked for, and it is also how they notice when
        the window was not the one they meant. */}
    <Caption>
      {`Read ${countOf(res.source_summary.count, 'entry', 'entries')} · ${
        res.source_summary.period_label
          ? `${res.source_summary.period_label} (${fmtDate(
              res.source_summary.from,
              tz
            )}–${fmtDate(res.source_summary.to, tz)})`
          : `${fmtDate(res.source_summary.from, tz)}–${fmtDate(
              res.source_summary.to,
              tz
            )}`
      }`}
    </Caption>
  </>
);

interface Props {
  res: AskResponse;
}

/**
 * WHICH CLOCK AN ANSWER IS RENDERED ON: THE ANSWER'S OWN (`tz`), on every
 * surface.
 *
 * The server writes the prose ("3 visitors came in yesterday") and cuts
 * "yesterday" on the clock it answers on — the building's for a concierge, the
 * reader's device for an owner or a manager (R-T12). The rows under the prose
 * are formatted HERE, so they must be on that same clock or the table
 * contradicts the sentence above it. That is why this asks for the response's
 * clock explicitly (`surface: 'app'`) instead of the surface's: at the desk an
 * owner's answer is still an owner's answer. It used to be handed the device's
 * zone by its parent, whoever was asking.
 *
 * The clock note is printed only for an answer that carries one: a refusal that
 * fired before the question was planned has no `tz` and shows no time.
 */
export const IqAnswer: React.FC<Props> = ({ res }) => {
  const clock = useClock({ surface: 'app', response: res });
  const tz = clock.zone;

  return (
    <>
      <AnswerBody res={res} tz={tz} />
      {res.supported && res.tz ? <ClockNote clock={clock} /> : null}
    </>
  );
};

const AnswerBody: React.FC<{ res: AskResponse; tz: string }> = ({ res, tz }) => {
  if (!res.supported) {
    // WP-19. A REFUSAL NOW CARRIES THE WAY FORWARD, and this component has to
    // render it or the fix is invisible where it matters most.
    //
    // `answer` on a refusal used to be the empty string, so this branch read
    // `unsupported_reason` and nothing else. The server now writes the reason
    // PLUS what IQ can answer instead into `answer` (iq.js refuse()), which is
    // the fix for the dead end owner decision D-20 calls the single worst
    // behaviour in the feature. `unsupported_reason` is still the fallback, so a
    // server that has not shipped the change renders exactly as before.
    return (
      <AnswerText>
        {res.answer || res.unsupported_reason || COPY.refusalFallback}
      </AnswerText>
    );
  }

  if (res.mode === 'list') return <ListAnswer res={res} tz={tz} />;
  if (res.mode === 'facts') return <FactsAnswer res={res} />;
  if (res.mode === 'semantic') return <SemanticAnswer res={res} tz={tz} />;

  // THE CLASSIC PLAN/COUNT SHAPE — a count computed in SQL, which is exact.
  // Its caption is reproduced BYTE-FOR-BYTE from the two panels this component
  // replaced, including ' · showing 10': that path really does sample ten ids
  // (iq.js:852), and WP-16 was told not to edit an existing user-facing string.
  // Only `AnswerText` is new here, and only so the \n survives.
  return (
    <>
      {/* A bare number is the shape that needs its scope stated most: nothing
          else on this branch says what it was counted over. */}
      <ScopeRow res={res} />
      <AnswerText>{res.answer}</AnswerText>
      {/* ---- THE LOG UNDER THE SENTENCE ----
          list_recent answers on THIS branch (it is a count metric with a
          phrasing call), and it now carries the rows the sentence is about —
          who came through, when, and to which unit. "Who entered today" is the
          question the desk asks most and it used to be answered with a number
          and a set of citation chips.

          Rendered UNDER the sentence rather than instead of it: the sentence is
          the summary and the table is the record, and a reader who wanted the
          count still has it on the line above. Guarded on length, so a server
          that has not shipped the change renders exactly as before. */}
      {res.metric === 'list_recent' && (res.rows || []).length > 0 ? (
        <RecentTable rows={(res.rows || []) as AskRecentRow[]} tz={tz} />
      ) : null}
      {res.source_summary.count > 0 ? (
        <Caption>
          Sources: {countOf(res.source_summary.count, 'entry', 'entries')},{' '}
          {fmtDate(res.source_summary.from, tz)}–
          {fmtDate(res.source_summary.to, tz)}
          {res.source_summary.sampled ? ' · showing 10' : ''}
        </Caption>
      ) : null}
    </>
  );
};
